export interface CustomerPriceGroupOption {
  id: number;
  label: string;
}

export interface CustomerCsvImportRow {
  rowNumber: number;
  name: string;
  att?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  ean?: string | null;
  cvr?: string | null;
  price_group?: string | null;
  price_group_id?: number | null;
  price_group_label?: string | null;
  isValid: boolean;
  error?: string;
}

export interface CustomerCsvParseResult {
  rows: CustomerCsvImportRow[];
  headers: string[];
  mappedFields: string[];
}

const FIELD_ALIASES: Record<string, string[]> = {
  name: ['navn', 'name', 'firmanavn', 'kunde', 'kundenavn'],
  att: ['att', 'attention', 'kontaktperson', 'att.', 'attn'],
  email: ['email', 'e-mail', 'mail', 'e mail'],
  phone: ['telefon', 'telefonnummer', 'phone', 'tlf', 'fastnet'],
  mobile: ['mobil', 'mobilnummer', 'mobile', 'mobiltlf'],
  address: ['adresse', 'address', 'gade', 'vej', 'street'],
  postal_code: ['postnummer', 'postnr', 'post nr', 'zip', 'postal', 'postal_code', 'postkode'],
  city: ['by', 'city', 'town', 'bynavn'],
  ean: ['ean', 'ean nummer'],
  cvr: ['cvr', 'cvr nr', 'cvr-nummer'],
  price_group: [
    'prisgruppe',
    'price group',
    'price_group',
    'pricegroup',
    'pris gruppe',
  ],
};

const EMPTY_PRICE_GROUP_VALUES = new Set([
  '',
  'ingen',
  'none',
  'null',
  '0',
  '-',
  'n/a',
  'na',
]);

const PREVIEW_COLUMNS: { key: keyof CustomerCsvImportRow; label: string }[] = [
  { key: 'name', label: 'Navn' },
  { key: 'att', label: 'Att.' },
  { key: 'email', label: 'E-mail' },
  { key: 'phone', label: 'Telefon' },
  { key: 'mobile', label: 'Mobil' },
  { key: 'address', label: 'Adresse' },
  { key: 'postal_code', label: 'Postnummer' },
  { key: 'city', label: 'By' },
  { key: 'ean', label: 'EAN' },
  { key: 'cvr', label: 'CVR' },
  { key: 'price_group', label: 'Prisgruppe' },
];

export function getCustomerCsvPreviewColumns() {
  return PREVIEW_COLUMNS;
}

/** Target fields available in the import column-mapping UI. */
export function getCustomerCsvFieldOptions(): {
  key: string;
  label: string;
}[] {
  return PREVIEW_COLUMNS.map(({ key, label }) => ({
    key: String(key),
    label,
  }));
}

export interface CustomerCsvFileStructure {
  headers: string[];
  dataLines: string[][];
  delimiter: string;
  columnMapping: (string | null)[];
}

export function buildColumnMappingFromHeaders(
  headers: string[]
): (string | null)[] {
  return headers.map((header) => mapHeaderToField(header));
}

export function mappedFieldsFromColumnMapping(
  columnMapping: (string | null)[]
): string[] {
  const seen = new Set<string>();
  const fields: string[] = [];
  for (const field of columnMapping) {
    if (field && !seen.has(field)) {
      seen.add(field);
      fields.push(field);
    }
  }
  return fields;
}

export function parseCustomerCsvStructure(
  content: string
): CustomerCsvFileStructure {
  const normalizedContent = content
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  const lines = normalizedContent
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return { headers: [], dataLines: [], delimiter: ',', columnMapping: [] };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = parseCsvLine(lines[0], delimiter);
  const columnMapping = buildColumnMappingFromHeaders(headers);
  const dataLines: string[][] = [];

  for (let i = 1; i < lines.length; i++) {
    dataLines.push(parseCsvLine(lines[i], delimiter));
  }

  return { headers, dataLines, delimiter, columnMapping };
}

export function applyCustomerCsvMapping(
  dataLines: string[][],
  columnMapping: (string | null)[],
  firstDataRowNumber = 2
): CustomerCsvImportRow[] {
  const rows: CustomerCsvImportRow[] = [];

  for (let i = 0; i < dataLines.length; i++) {
    const values = dataLines[i];
    const rowData: Record<string, string | null> = {};

    columnMapping.forEach((field, index) => {
      if (!field) return;
      const raw = values[index];
      const value =
        field === 'ean' || field === 'cvr'
          ? cleanIdentifierValue(raw)
          : cleanValue(raw);
      if (rowData[field] === undefined || rowData[field] === null) {
        rowData[field] = value;
      }
    });

    const name = rowData['name'] ?? '';
    const hasAnyValue = Object.values(rowData).some((value) => value !== null);
    if (!hasAnyValue) {
      continue;
    }

    const row: CustomerCsvImportRow = {
      rowNumber: firstDataRowNumber + i,
      name: name ?? '',
      att: rowData['att'],
      email: rowData['email'],
      phone: rowData['phone'],
      mobile: rowData['mobile'],
      address: rowData['address'],
      postal_code: rowData['postal_code'],
      city: rowData['city'],
      ean: rowData['ean'],
      cvr: rowData['cvr'],
      price_group: rowData['price_group'],
      isValid: name.trim().length > 0,
      error: name.trim().length > 0 ? undefined : 'Navn mangler',
    };

    rows.push(row);
  }

  return rows;
}

function normalizeHeader(value: string): string {
  return value
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ');
}

function detectDelimiter(line: string): ',' | ';' | '\t' {
  const counts = {
    ',': (line.match(/,/g) ?? []).length,
    ';': (line.match(/;/g) ?? []).length,
    '\t': (line.match(/\t/g) ?? []).length,
  };
  if (counts[';'] >= counts[','] && counts[';'] >= counts['\t']) return ';';
  if (counts['\t'] >= counts[','] && counts['\t'] >= counts[';']) return '\t';
  return ',';
}

function parseCsvLine(line: string, delimiter: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

function mapHeaderToField(header: string): string | null {
  const normalized = normalizeHeader(header);
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    if (aliases.includes(normalized)) {
      return field;
    }
  }
  return null;
}

function cleanValue(value: string | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/** Excel/CSV often exports EAN/CVR as scientific notation (e.g. 5,79801E+12). */
export function normalizeSpreadsheetNumericString(
  value: string | null | undefined
): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const compact = trimmed.replace(/\s/g, '');
  const sciPattern = /^[+-]?\d+[,.]?\d*[eE][+-]?\d+$/;
  if (sciPattern.test(compact)) {
    const num = Number(compact.replace(',', '.'));
    if (Number.isFinite(num)) {
      return Math.trunc(num).toString();
    }
  }

  const decimalPattern = /^[+-]?\d+[,.]\d+$/;
  if (decimalPattern.test(compact)) {
    const num = Number(compact.replace(',', '.'));
    if (Number.isFinite(num) && Math.trunc(num) === num) {
      return Math.trunc(num).toString();
    }
  }

  return trimmed;
}

function cleanIdentifierValue(value: string | undefined): string | null {
  const cleaned = cleanValue(value);
  if (!cleaned) return null;
  return normalizeSpreadsheetNumericString(cleaned) ?? cleaned;
}

export function formatCustomerAddress(row: {
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
}): string {
  const parts: string[] = [];
  const address = row.address?.trim();
  const postal = row.postal_code?.trim();
  const city = row.city?.trim();

  if (address) parts.push(address);
  if (postal && city) {
    parts.push(`${postal} ${city}`);
  } else if (postal) {
    parts.push(postal);
  } else if (city) {
    parts.push(city);
  }

  return parts.join(', ') || '—';
}

export function parseCustomerCsv(content: string): CustomerCsvParseResult {
  const structure = parseCustomerCsvStructure(content);
  const rows = applyCustomerCsvMapping(
    structure.dataLines,
    structure.columnMapping
  );

  return {
    rows,
    headers: structure.headers,
    mappedFields: mappedFieldsFromColumnMapping(structure.columnMapping),
  };
}

function normalizePriceGroupLookup(value: string): string {
  return value.trim().toLowerCase();
}

export function resolvePriceGroupId(
  raw: string | null | undefined,
  priceGroups: CustomerPriceGroupOption[]
): { id: number | null; label: string | null; error?: string } {
  if (raw === null || raw === undefined || raw.trim() === '') {
    return { id: null, label: null };
  }

  const trimmed = raw.trim();
  const normalized = normalizePriceGroupLookup(trimmed);
  if (EMPTY_PRICE_GROUP_VALUES.has(normalized)) {
    return { id: null, label: null };
  }

  if (/^\d+$/.test(trimmed)) {
    const id = Number(trimmed);
    const byId = priceGroups.find((group) => group.id === id);
    if (byId) {
      return { id: byId.id, label: byId.label };
    }
    return {
      id: null,
      label: null,
      error: `Ukendt prisgruppe-id: "${trimmed}"`,
    };
  }

  const byLabel = priceGroups.find(
    (group) => normalizePriceGroupLookup(group.label) === normalized
  );
  if (byLabel) {
    return { id: byLabel.id, label: byLabel.label };
  }

  const validLabels = priceGroups.map((group) => group.label);
  const validHint =
    validLabels.length > 0
      ? validLabels.join(', ')
      : 'Ingen (tom) – organisationen har ingen prisgrupper';

  return {
    id: null,
    label: null,
    error: `Ukendt prisgruppe: "${trimmed}". Gyldige værdier: ${validHint}`,
  };
}

export function validateCustomerCsvPriceGroups(
  rows: CustomerCsvImportRow[],
  priceGroups: CustomerPriceGroupOption[]
): CustomerCsvImportRow[] {
  return rows.map((row) => {
    const resolved = resolvePriceGroupId(row.price_group, priceGroups);

    if (resolved.error) {
      return {
        ...row,
        isValid: false,
        error: row.error ? `${row.error} · ${resolved.error}` : resolved.error,
      };
    }

    if (!row.isValid) {
      return row;
    }

    return {
      ...row,
      price_group_id: resolved.id,
      price_group_label: resolved.label,
    };
  });
}

export function formatPriceGroupPreview(row: CustomerCsvImportRow): string {
  if (row.price_group_label) {
    return row.price_group_label;
  }
  if (row.price_group?.trim()) {
    return row.price_group.trim();
  }
  return '—';
}

export function toCustomerImportPayload(row: CustomerCsvImportRow) {
  const payload: Record<string, unknown> = {
    name: row.name.trim(),
    att: row.att,
    email: row.email,
    phone: row.phone,
    mobile: row.mobile,
    address: row.address,
    postal_code: row.postal_code,
    city: row.city,
    ean: normalizeSpreadsheetNumericString(row.ean ?? undefined),
    cvr: normalizeSpreadsheetNumericString(row.cvr ?? undefined),
  };

  if (row.price_group_id != null && row.price_group_id > 0) {
    payload['price_group_id'] = row.price_group_id;
  }

  return payload;
}
